import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';

import 'swiper/css';

const NomerKg = new URL('../../assetts/images/nomer.webp', import.meta.url)
  .href;

// const kadamgroup = new URL('../../assetts/images/kadam.png', import.meta.url)
//   .href;

// const UiBazar = new URL('../../assetts/images/uibazar.png', import.meta.url)
//   .href;

const HidJaz = new URL('../../assetts/images/hidjaz.jpg', import.meta.url).href;

const PARTNERS: {
  name: string;
  url: string;
  logo?: string;
  /** Used instead of a logo image when we don't have brand artwork on file. */
  wordmark?: React.ReactNode;
}[] = [
  {
    name: 'Nomer.kg',
    url: 'https://nomer.kg/app/home',
    logo: NomerKg,
  },

  // {
  //   name: 'Үй Базар',
  //   url: 'https://www.instagram.com/uibazar_kg/',
  //   logo: UiBazar,
  // },

  // {
  //   name: 'Kadam Group',
  //   url: 'https://www.instagram.com/kadamgroup.kg/',
  //   logo: kadamgroup,
  // },

  {
    name: 'HidJaz',
    url: 'https://hidjaz-umra.kg/',
    logo: HidJaz,
  },

  {
    name: 'K-PRO',
    url: 'https://www.kpro.kg/',
    wordmark: (
      // Text wordmark until K-PRO sends their logo file (put it in
      // assetts/images and switch this entry to `logo:` like the others).
      <span className="text-2xl sm:text-3xl font-black tracking-tight text-slate-800">
        K-PRO<span className="text-blue-600">.kg</span>
      </span>
    ),
  },

  {
    name: 'Telephone.kg',
    url: 'https://telephone.kg/',
    wordmark: (
      <span className="flex items-center gap-2.5">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-500 text-white">
          <Phone size={18} strokeWidth={2.5} />
        </span>
        <span className="text-xl sm:text-2xl font-black tracking-tight text-gray-900">
          telephone<span className="text-emerald-600">.kg</span>
        </span>
      </span>
    ),
  },
];

export default function Partners() {
  const { t } = useTranslation();
  const swiperRef = useRef<SwiperType | null>(null);

  return (
    <section className="py-10 md:py-16 bg-gradient-to-b from-white to-slate-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <h2
            className="
              text-2xl
              sm:text-3xl
              md:text-4xl
              lg:text-5xl

              font-black
              tracking-tight

              text-gray-900

              mb-4
            "
          >
            {t('partners.title', 'Биздин өнөктөштөр')}
          </h2>

          <p
            className="
              text-gray-500

              text-sm
              sm:text-base
              md:text-lg

              leading-relaxed

              max-w-2xl
              mx-auto
            "
          >
            {t(
              'partners.subtitle',
              'Биз ишенимдүү өнөктөштөр менен бирге иштеп, сизге мыкты кызмат көрсөтүүгө умтулабыз.',
            )}
          </p>
        </div>

        {/* Decorative line */}
        <div className="relative hidden md:flex items-center justify-center mb-10 px-20">
          <div className="absolute inset-x-20 top-1/2 -translate-y-1/2 border-t border-dashed border-gray-200" />

          {[0, 1, 2].map((i) => (
            <div key={i} className="relative z-10 flex-1 flex justify-center">
              <div
                className={`
                  w-3 h-3 rounded-full shadow-sm
                  ${
                    i === 0
                      ? 'bg-blue-400'
                      : i === 1
                        ? 'bg-purple-300'
                        : 'bg-sky-300'
                  }
                `}
              />
            </div>
          ))}
        </div>

        {/* Slider */}
        <div className="relative">
          <div className="overflow-hidden">
            <Swiper
              modules={[Navigation, Autoplay]}
              loop
              autoplay={{
                delay: 3500,
                disableOnInteraction: false,
              }}
              speed={700}
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              slidesPerView={2}
              spaceBetween={12}
              breakpoints={{
                0: {
                  slidesPerView: 2,
                  spaceBetween: 12,
                },

                640: {
                  slidesPerView: 2,
                  spaceBetween: 16,
                },

                768: {
                  slidesPerView: 3,
                  spaceBetween: 18,
                },

                1024: {
                  slidesPerView: 4,
                  spaceBetween: 20,
                },

                1280: {
                  slidesPerView: 4,
                  spaceBetween: 24,
                },
              }}
            >
              {PARTNERS.map((partner, i) => (
                <SwiperSlide key={i}>
                  <a
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="
                      block

                      rounded-3xl

                      bg-white

                      border border-gray-100

                      shadow-sm

                      overflow-hidden
                    "
                  >
                    {/* Logo */}
                    <div
                      className="
                        h-32
                        sm:h-36
                        md:h-40

                        flex
                        items-center
                        justify-center

                        px-6
                        py-6
                      "
                    >
                      {partner.logo ? (
                        <img
                          src={partner.logo}
                          alt={partner.name}
                          loading="lazy"
                          decoding="async"
                          className="
                            max-h-16
                            sm:max-h-20

                            w-auto

                            object-contain
                          "
                        />
                      ) : (
                        partner.wordmark
                      )}
                    </div>
                  </a>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>
    </section>
  );
}
